import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const CACHE_KEY = 'duedate_permissions';

const DEFAULT_PERMS = {
  view_clients:   { manager: true,  consultant: true },
  add_client:     { manager: true,  consultant: false },
  edit_client:    { manager: true,  consultant: false },
  delete_client:  { manager: false, consultant: false },
  view_services:  { manager: true,  consultant: true },
  add_service:    { manager: true,  consultant: true },
  edit_service:   { manager: true,  consultant: true },
  delete_service: { manager: false, consultant: false },
  view_leads:     { manager: true,  consultant: false },
  manage_leads:   { manager: true,  consultant: false },
  export_data:    { manager: true,  consultant: false },
  view_reports:   { manager: true,  consultant: true },
  view_billing:   { manager: false, consultant: false },
};

// Structurally fixed — cannot be overridden by admin toggle
const FIXED_PERMS = {
  delete_client:  { manager: false, consultant: false },
  delete_service: { manager: false, consultant: false },
};

function loadCachedPerms() {
  try {
    const c = localStorage.getItem(CACHE_KEY);
    return c ? JSON.parse(c) : DEFAULT_PERMS;
  } catch { return DEFAULT_PERMS; }
}

function getHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

function readUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
}

export function usePermissions() {
  const user = readUser();
  const role = user.role || 'consultant';
  const [perms, setPerms] = useState(loadCachedPerms);

  const refresh = useCallback(() => {
    if (!localStorage.getItem('token')) return;
    axios.get(`${API}/permissions`, getHeaders())
      .then(r => {
        if (r.data && typeof r.data === 'object') {
          localStorage.setItem(CACHE_KEY, JSON.stringify(r.data));
          setPerms(r.data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function can(key) {
    if (role === 'admin') return true;
    if (key in FIXED_PERMS && !FIXED_PERMS[key][role]) return false;
    return !!(perms[key]?.[role]);
  }

  return {
    role,
    isAdmin:      role === 'admin',
    isManager:    role === 'manager',
    isConsultant: role === 'consultant',
    can,
    canDelete:        () => role === 'admin',
    canViewLeads:     () => can('view_leads'),
    canManageLeads:   () => can('manage_leads'),
    canAddClient:     () => can('add_client'),
    canEditClient:    () => can('edit_client'),
    canDeleteClient:  () => role === 'admin',
    canViewServices:  () => can('view_services'),
    canAddService:    () => can('add_service'),
    canEditService:   () => can('edit_service'),
    canDeleteService: () => role === 'admin',
    canExport:        () => can('export_data'),
    canViewReports:   () => can('view_reports'),
    canViewBilling:   () => can('view_billing'),
    perms,
    refresh,
  };
}

export default usePermissions;
